import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { black, lightBackground1, lightBlack } from '../../mainTheme';
import CustomerCareDTable from '../Shared/CustomerCareDTable';
import { getPharmacyNameFromToken, parseDateToLocal } from '../../shared/utils';
import { fetchPharmacyPatients } from '../../store/pharmacyPatients/actionCreators';

const PharmacyPatientOverview: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const { patients, isLoading } = useSelector(
    (state: RootState) => state.pharmacyPatients,
  );
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );

  // Pharmacy name comes from the logged-in user, then the JWT access token.
  const pharmacyName =
    dashboardUserInfo?.pharmacy_name ||
    getPharmacyNameFromToken() ||
    'Pharmacy';

  useEffect(() => {
    if (!patients?.length) {
      dispatch(fetchPharmacyPatients());
    }
  }, [dispatch, patients?.length]);

  const columns = [
    { field: 'patient_name', headerName: 'Patient', flex: 1 },
    {
      field: 'pharmacy_patient_id',
      headerName: 'Patient Pharmacy ID',
      flex: 0.8,
    },
    { field: 'age', headerName: 'Age', flex: 0.4 },
    { field: 'city', headerName: 'City', flex: 0.8 },
    { field: 'state', headerName: 'State', flex: 0.5 },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 0.8,
      renderCell: (params: any) =>
        params.value ? parseDateToLocal(params.value, 'MMM dd, yyyy') : 'N/A',
    },
  ];

  const rows = useMemo(
    () =>
      (patients || []).map((patient) => ({
        id: patient.patient_customer_id,
        patient_name:
          `${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
          'N/A',
        pharmacy_patient_id: patient.pharmacy_patient_id || 'N/A',
        age: patient.age ?? 'N/A',
        city: patient.city || 'N/A',
        state: patient.state || 'N/A',
        created_at: patient.created_at || null,
      })),
    [patients],
  );

  const filteredData = rows.filter((row) => {
    const term = searchTerm.toLowerCase();
    return (
      term === '' ||
      row.patient_name.toLowerCase().includes(term) ||
      String(row.pharmacy_patient_id).toLowerCase().includes(term) ||
      row.city.toLowerCase().includes(term) ||
      row.state.toLowerCase().includes(term)
    );
  });

  const handleRowClick = (row: { id: number }) => {
    navigate(`/v2/pharmacy/patients/${row.id}`);
  };

  return (
    <Box display="flex" flexDirection="column" bgcolor={lightBackground1}>
      <Box px={2} pt={3}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '22px',
            lineHeight: '30px',
            letterSpacing: '0.36px',
            color: lightBlack,
          }}
        >
          {pharmacyName}
        </Typography>
      </Box>
      <Box
        mt={1}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        p={2}
      >
        <Typography sx={{ color: lightBlack }}>
          <Box component="span" sx={{ color: black }}>
            {rows.length}
          </Box>
          {' Patients'}
        </Typography>
        <TextField
          placeholder="Search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <Box display="flex" alignItems="center" mr={1}>
                <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
              </Box>
            ),
            sx: {
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D4D4D9',
              },
              color: lightBlack,
              '& input::placeholder': {
                color: '#7F7F7F',
                opacity: 1,
              },
            },
          }}
          sx={{
            minWidth: '566px',
            '& .MuiInputLabel-root': {
              color: lightBlack,
            },
          }}
        />
      </Box>
      <Box px={2}>
        <Divider />
      </Box>

      <Box px={2} mt={2}>
        <CustomerCareDTable
          rows={filteredData}
          columns={columns}
          loading={isLoading}
          pageSize={10}
          defaultSortField="patient_name"
          defaultSortDirection="asc"
          hideViewAllButton
          minHeight="200px"
          onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  );
};

export default PharmacyPatientOverview;
